import type { JsonObject, ModelSchema } from "@nexra/contracts";
import type { LanguageSandboxResult } from "./api";
import { languageStudioControls } from "./language-studio-options";

const record = (value: unknown): JsonObject => value && typeof value === "object" && !Array.isArray(value) ? value as JsonObject : {};

function languageTokenLimitKey(protocol: string, schema?: JsonObject, input?: JsonObject): "max_tokens" | "max_completion_tokens" | "max_output_tokens" {
  if (protocol === "openai-responses") return "max_output_tokens";
  if (protocol !== "openai-chat-completions") return "max_tokens";
  const fields = record(schema?.properties);
  const completionOnly = "max_completion_tokens" in fields && !("max_tokens" in fields);
  const legacyOnly = "max_tokens" in fields && !("max_completion_tokens" in fields);
  if (completionOnly) return "max_completion_tokens";
  if (legacyOnly) return "max_tokens";
  if (input && "max_completion_tokens" in input) return "max_completion_tokens";
  return "max_completion_tokens" in fields ? "max_completion_tokens" : "max_tokens";
}

function reconcileLanguageTokenLimit(input: JsonObject, protocol: string, schema?: JsonObject): JsonObject {
  if (protocol !== "openai-chat-completions" || !schema) return input;
  const fields = record(schema.properties);
  const tokenKey = languageTokenLimitKey(protocol, schema, input);
  const staleKey = tokenKey === "max_completion_tokens" ? "max_tokens" : "max_completion_tokens";
  if (!(tokenKey in fields) || staleKey in fields || !(staleKey in input)) return input;
  if (!(tokenKey in input)) input[tokenKey] = input[staleKey];
  delete input[staleKey];
  return input;
}

export function languageResultStatus(result: LanguageSandboxResult): "running" | "failed" | "succeeded" {
  return result.demo?.stream?.status === "streaming" ? "running" : result.demo?.stream?.status === "failed" ? "failed" : "succeeded";
}

export function failedLanguageStream(result: LanguageSandboxResult | undefined): LanguageSandboxResult | undefined {
  return result?.demo?.stream?.status === "streaming" ? { ...result, demo: { ...result.demo, stream: { ...result.demo.stream, status: "failed" } } } : result;
}

/** The playground edits the selected endpoint's wire body, not a lossy intermediate model. */
export function initialLanguageInput(schema: ModelSchema | undefined, model: string, protocol: string, prompt: string): JsonObject {
  const sample = schema?.examples[0];
  const withDefaultStream = (input: JsonObject): JsonObject => protocol === "gemini-generate-content" ? input : { ...input, stream: true };
  if (sample && typeof sample === "object" && !Array.isArray(sample)) return reconcileLanguageTokenLimit(withDefaultStream(structuredClone(sample)), protocol, schema?.inputSchema);
  const defaults = schema?.defaults ?? {};
  if (protocol === "gemini-generate-content") return { ...defaults, contents: [{ role: "user", parts: [{ text: prompt }] }], generationConfig: { maxOutputTokens: 8192 } };
  if (protocol === "anthropic-messages") return withDefaultStream({ ...defaults, model, messages: [{ role: "user", content: prompt }], max_tokens: 8192 });
  if (protocol === "openai-chat-completions") {
    const tokenKey = languageTokenLimitKey(protocol, schema?.inputSchema);
    return reconcileLanguageTokenLimit(withDefaultStream({ ...defaults, model, messages: [{ role: "user", content: prompt }], [tokenKey]: 8192 }), protocol, schema?.inputSchema);
  }
  return withDefaultStream({ ...defaults, model, input: prompt, max_output_tokens: 8192, store: false });
}

export function replaceLanguagePrompt(input: JsonObject, prompt: string): JsonObject {
  if (Array.isArray(input.contents)) return { ...input, contents: [{ role: "user", parts: [{ text: prompt }] }] };
  if (Array.isArray(input.messages)) return { ...input, messages: [{ role: "user", content: prompt }] };
  if ("input" in input) return { ...input, input: prompt };
  for (const key of ["texts", "prompt", "text"]) if (key in input) return { ...input, [key]: prompt };
  return { ...input, texts: prompt };
}

/** Reassemble the native Message independently of the capped event preview. */
export function anthropicStreamMessage() {
  let message: JsonObject | undefined;
  let complete = false;
  const toolInputs = new Map<number, string>();
  return {
    push(value: unknown) {
      const event = record(value);
      if (event.type === "message_start") message = structuredClone(record(event.message));
      if (!message) return;
      const content = Array.isArray(message.content) ? message.content : (message.content = []);
      const index = Number(event.index);
      if (event.type === "content_block_start") content[index] = structuredClone(record(event.content_block));
      if (event.type === "content_block_delta") {
        const block = record(content[index]);
        const delta = record(event.delta);
        for (const key of ["text", "thinking", "signature"]) if (typeof delta[key] === "string") block[key] = String(block[key] ?? "") + delta[key];
        if (typeof delta.partial_json === "string") toolInputs.set(index, (toolInputs.get(index) ?? "") + delta.partial_json);
        if (delta.type === "citations_delta" && delta.citation) block.citations = [...(Array.isArray(block.citations) ? block.citations : []), delta.citation];
      }
      if (event.type === "content_block_stop" && toolInputs.has(index)) record(content[index]).input = JSON.parse(toolInputs.get(index)!);
      if (event.type === "message_delta") {
        Object.assign(message, record(event.delta));
        message.usage = { ...record(message.usage), ...record(event.usage) };
      }
      if (event.type === "message_stop") complete = true;
    },
    get message() { return message; },
    get complete() { return complete; },
  };
}

/** Assemble Chat's native response separately from the bounded event preview. */
export function chatStreamCompletion() {
  let completion: JsonObject | undefined;
  const choices = new Map<number, JsonObject>();
  const calls = new Map<number, Map<number, JsonObject>>();
  return {
    push(value: unknown) {
      const event = record(value);
      if (event.object !== "chat.completion.chunk" || !Array.isArray(event.choices)) return;
      const { choices: _chunks, ...metadata } = event;
      completion = { ...completion, ...metadata, object: "chat.completion", choices: [] };
      for (const raw of event.choices) {
        const chunk = record(raw);
        const index = Number(chunk.index ?? 0);
        const choice = choices.get(index) ?? { index, finish_reason: null, message: { role: "assistant", content: "" } };
        const message = record(choice.message);
        const delta = record(chunk.delta);
        const { delta: _delta, ...fields } = chunk;
        Object.assign(choice, fields);
        for (const [key, part] of Object.entries(delta)) {
          if (key === "tool_calls") continue;
          if (["content", "reasoning_content", "reasoning"].includes(key)) {
            if (typeof part === "string") message[key] = String(message[key] ?? "") + part;
            else if (!message[key]) message[key] = part;
          }
          else message[key] = structuredClone(part);
        }
        if (Array.isArray(delta.tool_calls)) {
          const byIndex = calls.get(index) ?? new Map<number, JsonObject>();
          for (const rawCall of delta.tool_calls) {
            const { index: callIndex, function: fragment, ...fields } = record(rawCall);
            const i = Number(callIndex ?? 0);
            const call = byIndex.get(i) ?? { function: { name: "", arguments: "" } };
            Object.assign(call, fields);
            const fn = record(call.function);
            for (const [key, part] of Object.entries(record(fragment))) fn[key] = typeof part === "string" && ["name", "arguments"].includes(key) ? String(fn[key] ?? "") + part : part;
            byIndex.set(i, call);
          }
          calls.set(index, byIndex);
          message.tool_calls = [...byIndex.entries()].sort(([a], [b]) => a - b).map(([, call]) => call);
        }
        choices.set(index, choice);
      }
      completion.choices = [...choices.entries()].sort(([a], [b]) => a - b).map(([, choice]) => choice);
    },
    get completion() { return completion; },
    get complete() { return choices.size > 0 && [...choices.values()].every(choice => typeof choice.finish_reason === "string"); },
  };
}

export function appendChatReply(input: JsonObject, result: LanguageSandboxResult, reply: string): JsonObject {
  const choice = record(Array.isArray(result.raw.choices) ? result.raw.choices[0] : undefined);
  const message = record(choice.message);
  if (message.role !== "assistant") throw new Error("未获得完整的原生消息，无法安全继续对话，请重新运行。");
  if (Array.isArray(message.tool_calls) && message.tool_calls.length) throw new Error("本轮仍有工具调用尚未执行，不能直接追加追问。请重新运行。");
  const history = Array.isArray(result.raw.messages) ? result.raw.messages : Array.isArray(input.messages) ? input.messages : [];
  return { ...input, messages: [...structuredClone(history), structuredClone(message), { role: "user", content: reply }] };
}

export function appendAnthropicReply(input: JsonObject, result: LanguageSandboxResult, reply: string): JsonObject {
  const message = record(result.raw.message ?? result.raw);
  if (!Array.isArray(message.content)) throw new Error("未获得完整的原生消息，无法安全继续对话，请重新运行。");
  if (message.content.some(block => record(block).type === "tool_use")) throw new Error("本轮仍有工具调用尚未执行，不能直接追加追问。请重新运行。");
  const history = Array.isArray(result.raw.messages) ? result.raw.messages : Array.isArray(input.messages) ? input.messages : [];
  return { ...input, messages: [...structuredClone(history), { role: "assistant", content: structuredClone(message.content) }, { role: "user", content: reply }] };
}

export function languageToolExecutions(result?: LanguageSandboxResult) {
  return result?.demo?.tools ?? (result?.demo?.tool ? [result.demo.tool] : []);
}

export function languageResultNotice(result?: LanguageSandboxResult): string | undefined {
  const choice = record(Array.isArray(result?.raw.choices) ? result.raw.choices[0] : undefined);
  if (choice.finish_reason === "length") return "已达到最大输出 Token 数，结果可能不完整。可提高上限后重新运行。";
  if (choice.finish_reason === "content_filter") return "本次输出被上游内容过滤，结果可能不完整。";
  const message = record(result?.raw.message ?? result?.raw);
  if (message.stop_reason === "max_tokens") return "已达到最大输出 Token 数，结果可能不完整。可提高上限后重新运行。";
  if (message.stop_reason === "refusal") return "模型拒绝了本次请求，请查看原始响应中的 stop_details。";
  if (message.stop_reason === "model_context_window_exceeded") return "已达到上下文窗口上限，结果可能不完整。";
}

export function studioLanguageValues(input: JsonObject, protocol: string, schema?: JsonObject) {
  const gemini = protocol === "gemini-generate-content";
  const messages = Array.isArray(input.messages) ? input.messages : [];
  const user = record(messages.findLast(message => record(message).role === "user"));
  const contents = Array.isArray(input.contents) ? input.contents : [];
  const parts = record(contents.findLast(content => record(content).role === "user")).parts;
  const text = (parts: unknown) => Array.isArray(parts) ? parts.map(part => record(part).text).filter(value => typeof value === "string").join("\n") : typeof parts === "string" ? parts : "";
  const config = record(input.generationConfig);
  const fields = record(schema?.properties);
  return {
    prompt: gemini ? text(parts) : protocol === "openai-responses" ? (typeof input.input === "string" ? input.input : text(record(Array.isArray(input.input) ? input.input.findLast(message => record(message).role === "user") : {}).content)) : text(user.content),
    systemInstruction: gemini ? text(record(input.systemInstruction).parts) : protocol === "openai-responses" ? String(input.instructions ?? "") : protocol === "anthropic-messages" ? text(input.system) : text(record(messages.find(message => record(message).role === "system")).content),
    temperature: Number((gemini ? config.temperature : input.temperature) ?? record(fields.temperature).default ?? 0.7),
    maxTokens: Number((gemini ? config.maxOutputTokens : input.max_output_tokens ?? input.max_completion_tokens ?? input.max_tokens) ?? 8192),
    effort: String((gemini ? record(config.thinkingConfig).thinkingLevel : input.reasoning_effort ?? record(input.reasoning).effort ?? record(input.output_config).effort) ?? languageStudioControls(schema, input).effortDefault ?? "high"),
    checks: (gemini ? record(config.thinkingConfig).includeThoughts === true : record(input.thinking).display === "summarized" || (record(input.thinking).type === "enabled" && !["none", "minimal"].includes(String(input.reasoning_effort)))) ? ["reasoning"] : [],
  };
}

/** These are native UI bindings, not an intermediary protocol. Schema reconciliation only repairs an incompatible token-limit alias. */
export function studioLanguageInput(input: JsonObject, protocol: string, values: { prompt: string; systemInstruction: string; temperature: number; maxTokens: number; effort?: string }, changed: ReadonlySet<string>, schema?: JsonObject): JsonObject {
  const next = reconcileLanguageTokenLimit(structuredClone(input), protocol, schema);
  const replaceText = (content: unknown, type: string) => {
    if (!Array.isArray(content)) return values.prompt;
    const index = content.findLastIndex(part => record(part).type === type || (type === "text" && typeof record(part).text === "string"));
    return index < 0 ? [...content, { type, text: values.prompt }] : content.map((part, i) => i === index ? { ...record(part), text: values.prompt } : part);
  };
  const patchMessage = (items: unknown, contentKey: string, type: string) => {
    const messages = Array.isArray(items) ? items : [];
    const index = messages.findLastIndex(message => record(message).role === "user");
    if (index < 0) return [...messages, { role: "user", [contentKey]: contentKey === "parts" ? [{ text: values.prompt }] : values.prompt }];
    return messages.map((message, i) => i === index ? { ...record(message), [contentKey]: replaceText(record(message)[contentKey], type) } : message);
  };
  if (changed.has("prompt")) {
    if (protocol === "gemini-generate-content") next.contents = patchMessage(next.contents, "parts", "text");
    else if (protocol === "openai-responses") next.input = typeof next.input === "string" ? values.prompt : patchMessage(next.input, "content", "input_text");
    else next.messages = patchMessage(next.messages, "content", "text");
  }
  if (changed.has("systemInstruction")) {
    if (protocol === "gemini-generate-content") next.systemInstruction = { ...record(next.systemInstruction), parts: [{ text: values.systemInstruction }] };
    else if (protocol === "anthropic-messages") next.system = values.systemInstruction;
    else if (protocol === "openai-responses") next.instructions = values.systemInstruction;
    else {
      const messages = Array.isArray(next.messages) ? next.messages : [];
      const index = messages.findIndex(message => record(message).role === "system");
      next.messages = index < 0 ? [{ role: "system", content: values.systemInstruction }, ...messages] : messages.map((message, i) => i === index ? { ...record(message), content: values.systemInstruction } : message);
    }
  }
  if (protocol === "gemini-generate-content") {
    if (changed.has("temperature") || changed.has("maxTokens") || changed.has("effort")) {
      const config = record(next.generationConfig);
      if (changed.has("temperature")) config.temperature = values.temperature;
      if (changed.has("maxTokens")) {
        if (Number.isFinite(values.maxTokens)) config.maxOutputTokens = values.maxTokens;
        else delete config.maxOutputTokens;
      }
      if (changed.has("effort") && values.effort) config.thinkingConfig = { ...record(config.thinkingConfig), thinkingLevel: values.effort };
      next.generationConfig = config;
    }
  } else {
    if (changed.has("temperature")) next.temperature = values.temperature;
    if (changed.has("maxTokens")) {
      const tokenKey = languageTokenLimitKey(protocol, schema, next);
      if (Number.isFinite(values.maxTokens)) next[tokenKey] = values.maxTokens;
      else delete next[tokenKey];
    }
  }
  if (protocol === "anthropic-messages" && changed.has("effort") && values.effort) next.output_config = { ...record(next.output_config), effort: values.effort };
  if (protocol === "openai-chat-completions" && changed.has("effort") && values.effort) next.reasoning_effort = values.effort;
  if (protocol === "openai-responses" && changed.has("effort") && values.effort) next.reasoning = { ...record(next.reasoning), effort: values.effort };
  return next;
}

/** Display native conversation text without altering the request's content parts. */
export function studioConversation(input: JsonObject): { role: "user" | "assistant"; content: string }[] {
  const source = input.messages ?? input.contents ?? input.input;
  if (typeof source === "string") return [{ role: "user", content: source }];
  if (!Array.isArray(source)) return [];
  return source.flatMap(raw => {
    const item = record(raw);
    const role = item.role === "model" ? "assistant" : item.role;
    if (role !== "user" && role !== "assistant") return [];
    const parts = item.content ?? item.parts;
    const content = typeof parts === "string" ? parts : Array.isArray(parts) ? parts.map(part => typeof record(part).text === "string" ? record(part).text : "").join("\n") : "";
    return [{ role, content }];
  });
}

export function studioCurrentPrompt(input: JsonObject): string {
  return studioConversation(input).findLast(message => message.role === "user")?.content ?? "";
}
